import React, { useEffect, useState } from "react";
import "../MediaPreview.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Col, Row, Modal, Button, Alert } from "antd";
import { OPP_CONSTANTS } from "../constants/OppContants";
import { humaniseBytes } from "../utils/formatters";
import { addResourceFiles, getSignedURL } from "../services/fetchApis";
import { uploadFileviaSignedURL } from "../services/mediaClient";

function MediaPreview({
  showMediaPreview,
  shipmentResources = [],
  windowWidth,
  windowHeight,
  handlePreviewClose,
  handleAttahmentsOpen,
  orderId,
  handleUploadSuccess,
}) {
  const [selectedResource, setSelectedResource] = useState(null);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const MAX_LIMIT = OPP_CONSTANTS.resources.MAX_LIMIT;
  const MAX_FILE_SIZE = OPP_CONSTANTS.resources.MAX_FILE_SIZE;

  useEffect(() => {
    const processedFiles = shipmentResources.map((res) => ({
      ...res,
      isUploaded: true,
      isSynced: true,
    }));
    setMediaFiles(processedFiles);
    setSelectedResource(processedFiles[0] || null);
  }, [shipmentResources]);

  const addMediaFile = (file) => {
    const duplicate = mediaFiles.some(
      (media) => media.fileName === file.fileName
    );
    if (duplicate) {
      setErrorMessage(OPP_CONSTANTS.messages.duplicateMedia);
      return;
    }
    setMediaFiles((prev) => [...prev, file]);
  };

  const removeMediaFile = (fileToRemove) => {
    setMediaFiles((prev) =>
      prev.filter((f) => f.fileName !== fileToRemove.fileName)
    );
    setErrorMessage(null);
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    setErrorMessage(null);

    let validFiles = files.filter((file) => {
      return (
        file.type.startsWith("image/") ||
        file.type.startsWith("video/") ||
        file.type.startsWith("application/")
      );
    });

    if (validFiles.length) {
      validFiles = validFiles.filter((file) => {
        return file.size < MAX_FILE_SIZE;
      });

      if (!validFiles.length) {
        setErrorMessage(
          OPP_CONSTANTS.messages.exceededMediaLimit.replace(
            /{max_size}/,
            humaniseBytes(MAX_FILE_SIZE)
          )
        );
        return;
      }
    }

    if (validFiles.length + mediaFiles.length > MAX_LIMIT) {
      setErrorMessage(
        OPP_CONSTANTS.messages.exceedYamllimit.replace(
          /{max_count}/,
          MAX_LIMIT
        )
      );
      return;
    }

    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        addMediaFile({
          file,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          src: reader.result,
          isUploaded: false,
          isSynced: false,
          cdnPath: null,
          cdnUrl: null,
        });
      };
      reader.readAsDataURL(file);
    });

    event.target.value = null;
  };

  const uploadAllFilesToBucket = async () => {
    setIsUploading(true);
    const processedFiles = [];

    for (let i = 0; i < mediaFiles.length; i++) {
      let file = mediaFiles[i];

      if (file.isUploaded) {
        processedFiles.push(file);
        continue;
      }

      let uploadStatus = false;
      let signedUrlObject;

      try {
        signedUrlObject = await getSignedURL(
          orderId,
          file.fileName,
          file.fileType
        );

        if (signedUrlObject && signedUrlObject.data) {
          uploadStatus = await uploadFileviaSignedURL(
            signedUrlObject.data,
            file
          );
        }

        if (uploadStatus) {
          file = {
            ...file,
            isUploaded: true,
            cdnPath: signedUrlObject.key,
            cdnUrl: process.env.REACT_APP_AWS_URL + signedUrlObject.key
          };
        } else {
          let errorMsg = OPP_CONSTANTS.messages.failedToUpload;
          if (
            signedUrlObject &&
            signedUrlObject.toString().includes("TypeError:") &&
            signedUrlObject.toString().includes("ISO-")
          ) {
            errorMsg = OPP_CONSTANTS.messages.invalidFileName;
          }
          setErrorMessage(errorMsg);
        }
      } catch (err) {
        setErrorMessage(OPP_CONSTANTS.messages.failedToUpload);
      }

      processedFiles.push(file);
    }

    setMediaFiles(processedFiles);
    return processedFiles;
  };

  const handleSubmit = async () => {
    setErrorMessage(null);

    if (mediaFiles && mediaFiles.length > 0) {
      const uploadedMediaFiles = await uploadAllFilesToBucket();

      if (uploadedMediaFiles.length > 0) {
        const cleaned = uploadedMediaFiles.map(
          ({ file, isSynced, isUploaded, src, ...rest }) => rest
        );

        const mediaFinalList = cleaned.filter((f) => f.cdnPath != null);

        if (mediaFinalList.length > 0) {
          const response = await addResourceFiles(orderId, mediaFinalList);
          if (response && response.status === "OK") {
            const synced = uploadedMediaFiles.map((file) =>
              file.isUploaded ? { ...file, isSynced: true } : file
            );
            setMediaFiles(synced);
            handleUploadSuccess(synced.filter((f) => f.isSynced));
          } else {
            setErrorMessage(OPP_CONSTANTS.messages.failToSubmit);
          }
        }
      }
    }

    setIsUploading(false);
  };

  const handleDelete = (resource) => {
    if (!resource.isSynced) {
      removeMediaFile(resource);
    } else {
      Modal.confirm({
        title: "Delete File",
        content: `Are you sure you want to delete "${resource.fileName}"?`,
        okText: "Delete",
        okButtonProps: { danger: true },
        cancelText: "Cancel",
        onOk: async () => {
          try {
            const updatedList = mediaFiles
              .filter((r) => r.fileName !== resource.fileName)
              .map(
                ({ file, isUploaded, isSynced, src, ...rest }) => rest
              );
            const response = await addResourceFiles(orderId, updatedList);
            if (response?.status === "OK") {
              setMediaFiles((prev) =>
                prev.filter((r) => r.fileName !== resource.fileName)
              );
              handleUploadSuccess(updatedList);
            } else {
              setErrorMessage("Failed to delete the file.");
            }
          } catch (err) {
            setErrorMessage("Delete error occurred. Try again.");
          }
        },
      });
    }
  };

  const handleClose = () => {
    const hasUnsaved = mediaFiles.some((f) => !f.isSynced);
    const removeUnsaved = () => {
      setMediaFiles((prev) => prev.filter((f) => f.isSynced));
      handlePreviewClose();
    };
    if (hasUnsaved) {
      Modal.confirm({
        title: "Unsaved Uploads",
        content: "You have unsaved uploads. Are you sure you want to close? Unsaved files will be discarded.",
        okText: "Yes, Close",
        cancelText: "Cancel",
        onOk: removeUnsaved,
      });
    } else {
      removeUnsaved();
    }
  };

  const allResources = mediaFiles;

  const sortedFiles = [...mediaFiles].sort((a, b) => {
    if (a.isSynced === b.isSynced) return 0;
    return a.isSynced ? 1 : -1; // pending first
  });

  return (
    <Modal
      title="Gallery"
      open={showMediaPreview}
      onCancel={handleClose}
      footer={
        <Button
          type="primary"
          onClick={handleSubmit}
          disabled={
            isUploading ||
            mediaFiles.length === 0 ||
            mediaFiles.every((f) => f.isSynced)
          }
          icon={<FontAwesomeIcon icon={isUploading ? "spinner" : "upload"} spin={isUploading} />}
        >
          {isUploading ? "Uploading..." : "Upload Files"}
        </Button>
      }
      width={windowWidth > 900 ? 1200 : 700}
      className="media-modal-container"
    >
      <div className="gallery-body-container">
        <div className="file-list-container">
          <input
            type="file"
            id="file-upload"
            style={{ display: "none" }}
            multiple
            accept="image/*,video/*,application/pdf"
            onChange={handleFileUpload}
          />
          <div className="add-files-section">
            <Button
              className="add-files-button"
              disabled={mediaFiles.length === MAX_LIMIT}
              onClick={() => document.getElementById("file-upload").click()}
            >
              <FontAwesomeIcon icon="fa-circle-plus" />  Add Files
            </Button>
            <div className="file-info-footer">
              <div>(Max file size: {humaniseBytes(MAX_FILE_SIZE)})</div>
              <div>
                {mediaFiles.length} / {MAX_LIMIT}
              </div>
            </div>
            {errorMessage && (
              <div style={{ width: '100%', marginTop: 10 }}>
                <Alert
                  message={errorMessage}
                  type="error"
                  showIcon
                  closable
                  onClose={() => setErrorMessage(null)}
                  style={{ width: '100%' }}
                />
              </div>
            )}
          </div>

          {sortedFiles.map((file) => (
            <div
              key={file.fileName}
              className={`file-item ${selectedResource?.fileName === file.fileName ? "selected" : ""
                }`}
              onClick={() => setSelectedResource(file)}
            >
              <div className="file-info">
                <div className="file-name">{file.fileName}</div>
                <div className="file-size">{(file.fileSize / 1024).toFixed(0)} KB</div>
              </div>
              <div className="file-actions">
                {file.isSynced ? (
                  <FontAwesomeIcon icon="circle-check" className="status-icon" />
                ) : (
                  <span className="pending-badge">Pending</span>
                )}
                <Button
                  type="text"
                  className="delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(file);
                  }}
                >
                  <FontAwesomeIcon icon="trash" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="preview-panel">
          {selectedResource ? (
            <>
              {selectedResource.fileType.startsWith("image/") ? (
                <img src={selectedResource.cdnUrl || selectedResource.src} className="preview-image" />
              ) : selectedResource.fileType.startsWith("video/") ? (
                <video controls className="preview-video">
                  <source src={selectedResource.cdnUrl || selectedResource.src} />
                </video>
              ) : selectedResource.fileType === "application/pdf" ? (
                <iframe
                  src={selectedResource.cdnUrl || selectedResource.src}
                  className="preview-pdf"
                  title={selectedResource.fileName}
                />
              ) : (
                <div className="unsupported-preview">No preview available</div>
              )}
              {!selectedResource.isSynced && (
                <div style={{ color: '#FF7929', marginTop: 12, fontWeight: 500, fontSize: 16 }}>
                  {OPP_CONSTANTS.messages.unsavedFile}
                </div>
              )}
            </>
          ) : (
            <div className="preview-placeholder">Select a file to preview</div>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default MediaPreview;
